import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-emerald-950 tmf-grain flex flex-col">
      <div className="p-6"><button onClick={() => navigate("/login")} className="text-emerald-200 flex items-center gap-2 text-sm"><ArrowLeft className="w-4 h-4" /> Back to Login</button></div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl tmf-rise">
          <Logo showTagline />
          {!sent ? (
            <>
              <h1 className="mt-6 font-display font-bold text-2xl text-emerald-950">Forgot your password?</h1>
              <p className="mt-1 text-sm text-gray-600">Enter your registered email and we'll send you a secure link to reset it.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label>Email</Label>
                  <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 h-12" />
                </div>
                <Button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 rounded-full">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send Reset Link"}
                </Button>
              </form>
            </>
          ) : (
            <div className="mt-6 text-center" data-testid="forgot-sent">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center"><MailCheck className="w-7 h-7 text-emerald-600" /></div>
              <h1 className="mt-4 font-display font-bold text-xl text-emerald-950">Check your email</h1>
              <p className="mt-2 text-sm text-gray-600">If an account exists for <b>{email}</b>, a password reset link has been sent. The link expires in 1 hour.</p>
              <Button onClick={() => navigate("/login")} className="mt-6 w-full h-12 bg-emerald-600 hover:bg-emerald-700 rounded-full">Return to Login</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
